/**
 * Owns the default composite (wide, multi-station) view: the server-baked
 * CONUS mosaic (workers/CONTRACTS.md "Mosaic-head" — `mosaic/conus.json` +
 * `mosaic/conus/{ts}.tif`). Manifest polling (~5s, aligned with the
 * manifest's own max-age=5), whole-file COG fetch + decode at a zoom-chosen
 * overview level (reusing fusionGeo.ts's overviewLevelForZoom AND
 * fusionDecoder.ts/fusionDecodeWorker.ts's off-main-thread station decode
 * verbatim — the mosaic's z7 grid is THE SAME lattice every station bakes
 * onto, workers/CONTRACTS.md, so both the LOD math and the decode plumbing
 * are station-agnostic already), client-side palette + display-floor
 * colorization (mosaicColorize.ts), a two-layer crossfade so frame swaps and
 * LOD changes never hard-cut, preloading, and dev-mode perf instrumentation.
 *
 * Deliberately NOT windowed (contrast the old, reverted mosaicController.ts
 * this replaces, which read HTTP-Range pixel windows off a hand-maintained
 * grid-constant module): mosaic frames are 84-186 KB in production (7
 * stations, 2026-08-10) — the same "whole-file beats windowed" regime
 * fusionDecodeWorker.ts's doc comment measured for station COGs, not the
 * ~700 KB regime the old windowed reader was built for. There is also no
 * client-side grid-geometry module anymore (the old mosaicGrid.ts): every
 * frame's own bbox is read straight off its COG's IFD 0 tiepoint via
 * geotiff.js, exactly like a station frame's bbox always has been — nothing
 * here needs to know the mosaic's real-world origin/dimensions in advance,
 * which also means this file can never drift out of sync with the backend's
 * actual grid the way a hardcoded constant could.
 *
 * Lifecycle shape (init/start/stop/setActive/goLive/showAtOrBefore) mirrors
 * the old mosaicController.ts on purpose — app.ts's transport/HUD wiring
 * around a composite-view controller doesn't need to change shape, only
 * which controller it's wired to for a given compositeRenderMode.
 */
import type * as maplibregl from "maplibre-gl";
import { mosaicManifestUrl, mosaicFrameUrl, fleetManifestUrl } from "./config";
import { ManifestPoller } from "./manifestPoller";
import type { MosaicHotManifest, MosaicManifestFrame, ContributingStation } from "./mosaicTypes";
import type { FleetManifest } from "./fleetTypes";
import { STATIONS } from "./stations";
import { overviewLevelForZoom, type OverviewLevel } from "./fusionGeo";
import { FusionDecoder, type StationDecodeResult } from "./fusionDecoder";
import { PRECIP_FLOOR_RAW, NO_FLOOR_RAW } from "./fusionCompositor";
import { CROSSFADE_MS } from "./fusionController";
import { mosaicFrameCache, decodeCacheKey, getDecodeResult, setDecodeResult } from "./mosaicFrameCache";
import { cornersFromBBox3857, cornersToMapLibreCoordinates } from "./geo";
import { colorizeMosaicRaster } from "./mosaicColorize";
import { resolveTargetTs } from "./mosaicTimeline";
import { frameAtOrBefore, findNearestUsable } from "./scrub";
import { timeAsync, timeSync } from "./perfStages";

/** How many manifest frames outward (in each direction) showNearestLoaded()
 * will search for a cache hit before giving up — a drag rarely lands more
 * than a couple of frames from the last decoded one, so this stays small
 * and O(1)-ish in practice; bounded so a huge scrub jump can't turn an
 * "instant feedback" call into a linear scan of the whole manifest. */
const NEAREST_LOADED_SEARCH_RADIUS = 8;

const SOURCE_IDS = ["radar-mosaic-a", "radar-mosaic-b"] as const;
const layerIdFor = (sourceId: string): string => `${sourceId}-layer`;

const MOSAIC_OPACITY = 0.85;
/** Aligned with the manifest's own `max-age=5` (workers/CONTRACTS.md). */
const MANIFEST_POLL_INTERVAL_MS = 5_000;
/** fleet.json roster poll — same cadence FusionController already uses for
 * the identical purpose (its own fleet.json poller). */
const FLEET_POLL_INTERVAL_MS = 10_000;
const PRELOAD_AHEAD = 2;

const TRANSPARENT_PIXEL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII=";
/** Small non-degenerate placeholder quad (mirrors fusionController.ts's own
 * PLACEHOLDER_COORDS) — a zero-area quad crashes MapLibre's internal
 * Mercator transform setup on load, but opacity starts at 0 either way, so
 * the exact placeholder location is irrelevant until the first real decode
 * paints real coordinates. */
const PLACEHOLDER_COORDS: [[number, number], [number, number], [number, number], [number, number]] = [
  [-1, 1],
  [1, 1],
  [1, -1],
  [-1, -1],
];

const DEV = import.meta.env.DEV;

export class MosaicController {
  private poller: ManifestPoller<MosaicHotManifest>;
  private fleetPoller: ManifestPoller<FleetManifest>;
  private decoder = new FusionDecoder();

  private manifestValue: MosaicHotManifest | null = null;
  private fleetIds: string[] | null = null;

  private active = false;
  private liveMode = true;
  private lastScrubMs: number | null = null;
  private floorEnabled = true;

  private activeSlot: 0 | 1 = 0;
  private layersReady = false;

  private currentTs: string | null = null;
  private currentLevel: OverviewLevel | null = null;
  private currentFrame: MosaicManifestFrame | null = null;
  /** The last successful decode — kept around so a floor toggle can
   * re-colorize and repaint without a redundant fetch/decode. */
  private lastResult: StationDecodeResult | null = null;

  /** Monotonic token so a slower, superseded swapTo() (e.g. a live-mode
   * manifest update or a re-window arriving mid-decode) can't paint over a
   * fresher one that resolved first — same pattern as app.ts's loadToken. */
  private requestToken = 0;

  private firstPaintLogged = false;

  constructor(
    private map: maplibregl.Map,
    private getBaseUrl: () => string,
    private onDataChanged: () => void,
  ) {
    this.poller = new ManifestPoller<MosaicHotManifest>(
      () => mosaicManifestUrl(this.getBaseUrl()),
      (m) => this.onManifest(m),
      () => {
        // mosaic/conus.json absent or erroring: "it may deploy after you" —
        // same philosophy as app.ts's onCompositeManifestError. Keep the
        // last-known frame on screen; manifestValue only ever goes back to
        // null if it started that way (never heard from it at all).
      },
      MANIFEST_POLL_INTERVAL_MS,
    );
    this.fleetPoller = new ManifestPoller<FleetManifest>(
      () => fleetManifestUrl(this.getBaseUrl()),
      (m) => {
        this.fleetIds = m.stations.map((s) => s.id);
        this.onDataChanged();
      },
      () => {
        // Not deployed yet: stationRoster() falls back to the static STATIONS list.
        this.fleetIds = null;
        this.onDataChanged();
      },
      FLEET_POLL_INTERVAL_MS,
    );
  }

  /** Call once, after the map's "load" event (mirrors fusionController.ts's
   * init()/app.ts's createCompositeLayers). Adds the two ping-pong image
   * layers and starts the internal moveend/zoomend re-decode listeners. */
  init(): void {
    if (this.layersReady) return;
    for (const sourceId of SOURCE_IDS) {
      this.map.addSource(sourceId, { type: "image", url: TRANSPARENT_PIXEL, coordinates: PLACEHOLDER_COORDS });
      const layerId = layerIdFor(sourceId);
      this.map.addLayer({
        id: layerId,
        type: "raster",
        source: sourceId,
        // "nearest", not "linear": mosaic pixels are byte-identical to
        // station pixels by construction (workers/CONTRACTS.md's "z7 paste
        // alignment" — never a blend/interpolation), so the same pixelated-
        // honesty rendering choice station drill-down makes (renderFrame.ts's
        // doc comment) applies here too. Smoothing is a separate, deliberately
        // NOT-done-here follow-up.
        paint: { "raster-opacity": 0, "raster-resampling": "nearest" },
      });
      this.map.setPaintProperty(layerId, "raster-opacity-transition", { duration: CROSSFADE_MS });
    }
    this.layersReady = true;

    this.map.on("moveend", () => this.onViewportSettled());
    this.map.on("zoomend", () => this.onViewportSettled());
  }

  start(): void {
    this.poller.start();
    this.fleetPoller.start();
  }

  stop(): void {
    this.poller.stop();
    this.fleetPoller.stop();
  }

  manifest(): MosaicHotManifest | null {
    return this.manifestValue;
  }

  /** Full station roster for the strip's offline derivation: fleet.json's
   * list when available, else the static STATIONS ids (same fallback
   * philosophy as fusionController.ts's currentStationsList()). */
  stationRoster(): string[] {
    return this.fleetIds ?? STATIONS.map((s) => s.id);
  }

  /** The CURRENTLY DISPLAYED mosaic frame's contributing list (live latest,
   * or wherever scrub/loop has landed) — app.ts's renderStationStrip() reads
   * this, not manifestValue.latest, so the strip always matches what's
   * actually on screen. */
  currentContributing(): ContributingStation[] {
    return this.currentFrame?.contributing ?? [];
  }

  currentFrameBakeTimeMs(): number | null {
    return this.currentFrame?.bakeTimeMs ?? null;
  }

  /** The mosaic's OWN compose pipeline latency for the currently displayed
   * frame — shown in the hero panel instead of any one contributing
   * station's latency (which the contributing list doesn't carry anyway). */
  currentFramePipelineLatencyMs(): number | null {
    return this.currentFrame ? this.currentFrame.bakeTimeMs - this.currentFrame.dataTimeLastMs : null;
  }

  /** Trailing-window scrub bounds from the mosaic's own frames list — a
   * single manifest, unlike the fusion path's globalTimeRange() over N
   * independent per-station manifests (scrub.ts). */
  timeRange(): { minMs: number; maxMs: number } | null {
    const frames = this.manifestValue?.frames;
    if (!frames || frames.length === 0) return null;
    return { minMs: frames[0].dataTimeLastMs, maxMs: frames[frames.length - 1].dataTimeLastMs };
  }

  getFloorEnabled(): boolean {
    return this.floorEnabled;
  }

  setFloorEnabled(enabled: boolean): void {
    if (this.floorEnabled === enabled) return;
    this.floorEnabled = enabled;
    if (this.lastResult) this.paint(this.lastResult);
  }

  /** app.ts calls this false at the start of a scrubber drag and true once
   * it settles: the 150ms raster-opacity-transition (set once at init(),
   * below) exists so ordinary frame swaps and LIVE transitions never hard-
   * cut, but it actively HURTS the scrubber's "nearest loaded" instant-
   * feedback repaints (showNearestLoaded(), called on every drag tick) —
   * a fade-in on top of rapid successive repaints looks smeared/soft
   * instead of instant, undermining the whole point. No-op before
   * layersReady (there's nothing to set the property on yet). */
  setCrossfadeEnabled(enabled: boolean): void {
    if (!this.layersReady) return;
    const duration = enabled ? CROSSFADE_MS : 0;
    for (const sourceId of SOURCE_IDS) {
      this.map.setPaintProperty(layerIdFor(sourceId), "raster-opacity-transition", { duration });
    }
  }

  /** Toggles whether the mosaic layer is the visible one (app.ts's render-
   * mode switch between mosaic and the dev-only fusion path). Crossfades
   * in/out via the same raster-opacity-transition as frame swaps. */
  setActive(active: boolean): void {
    if (this.active === active) return;
    this.active = active;
    if (this.layersReady) {
      this.map.setPaintProperty(layerIdFor(SOURCE_IDS[this.activeSlot]), "raster-opacity", active ? MOSAIC_OPACITY : 0);
    }
    if (active) this.refresh();
  }

  goLive(): void {
    this.liveMode = true;
    if (this.active) this.refresh();
  }

  /** Shared scrub/loop entry point — same time axis as app.ts's
   * compositeScrubTimeMs, matched via the mosaic's own frames list
   * (frameAtOrBefore, scrub.ts). */
  showAtOrBefore(ms: number): void {
    this.liveMode = false;
    this.lastScrubMs = ms;
    if (!this.active) return;
    this.refresh();
    if (this.currentLevel !== null) this.preloadAhead(ms, this.currentLevel);
  }

  /** Instant, CACHE-ONLY repaint for app.ts's scrubber "input" handler
   * WHILE dragging — unlike showAtOrBefore()/refresh(), this never
   * triggers a fetch/decode: it searches outward from the frame at-or-
   * before `ms` for the nearest one whose decode is already in
   * mosaicFrameCache and repaints with that, so the display keeps moving
   * during a fast drag even before the EXACT target (kicked off
   * separately, once the drag settles — app.ts's debounce) has finished
   * decoding. A no-op if nothing nearby is cached yet, or if the nearest
   * cached frame is already what's on screen. */
  showNearestLoaded(ms: number): void {
    if (!this.active) return;
    const frames = this.manifestValue?.frames;
    if (!frames || frames.length === 0) return;
    const level = overviewLevelForZoom(this.map.getZoom());
    const atOrBefore = frameAtOrBefore(frames, ms);
    const startIdx = atOrBefore ? frames.indexOf(atOrBefore) : frames.length - 1;

    const nearest = findNearestUsable(frames, startIdx, NEAREST_LOADED_SEARCH_RADIUS, (f) => mosaicFrameCache.has(decodeCacheKey(f.ts, level)));
    if (!nearest || (nearest.ts === this.currentTs && level === this.currentLevel)) return; // nothing cached nearby, or already showing the nearest one

    const cached = getDecodeResult(mosaicFrameCache, nearest.ts, level);
    if (!cached) return; // defensive — the predicate above already confirmed a cache hit
    this.lastResult = cached;
    this.paint(cached);
    this.currentTs = nearest.ts;
    this.currentLevel = level;
    this.currentFrame = nearest;
    this.onDataChanged();
  }

  private onManifest(manifest: MosaicHotManifest): void {
    this.manifestValue = manifest;
    this.onDataChanged();
    if (this.active) this.refresh();
  }

  private onViewportSettled(): void {
    if (!this.active) return;
    this.refresh();
  }

  /** Single funnel for "what should be on screen right now, at the current
   * zoom's LOD": live mode wants the manifest's latest ts, scrub mode wants
   * frameAtOrBefore(lastScrubMs), and either way a level change alone (pure
   * zoom, same ts) still needs a re-decode. Called on activation, manifest
   * updates, goLive(), showAtOrBefore(), and viewport settle — one path
   * instead of four near-duplicate ones (mirrors the old, reverted
   * mosaicController.ts's refreshOnActivate(), generalized to every call
   * site instead of just the activate transition). */
  private refresh(): void {
    const targetTs = timeSync("manifest-resolve", () => resolveTargetTs(this.manifestValue, this.liveMode, this.lastScrubMs));
    if (!targetTs) {
      this.clearFrame();
      return;
    }
    const level = overviewLevelForZoom(this.map.getZoom());
    if (targetTs === this.currentTs && level === this.currentLevel) return; // already exactly this
    void this.swapTo(targetTs);
  }

  private async swapTo(ts: string): Promise<void> {
    if (!this.layersReady) return;
    const token = ++this.requestToken;
    const level = overviewLevelForZoom(this.map.getZoom());
    const key = decodeCacheKey(ts, level);
    const swapStart = performance.now();
    let result = getDecodeResult(mosaicFrameCache, ts, level);
    if (!result) {
      try {
        result = await timeAsync(
          "fetch",
          () => this.decoder.decode("MOSAIC", mosaicFrameUrl(this.getBaseUrl(), ts), level, key),
          (r) => `IFD${level},${(r.bytesTransferred / 1024).toFixed(0)}KiB`,
        );
        // Cache the result even if this request has since been superseded
        // (checked below) — the decode still happened and is still valid
        // for `key`; discarding it would waste the work a scrub-back could
        // otherwise reuse for free.
        setDecodeResult(mosaicFrameCache, ts, level, result);
        this.logBytes(key, result.bytesTransferred);
      } catch {
        return; // best-effort — leave whatever's currently painted
      }
    }
    if (token !== this.requestToken) return; // superseded — a fresher swapTo already won

    this.lastResult = result;
    this.paint(result);
    this.currentTs = ts;
    this.currentLevel = level;
    this.currentFrame = this.manifestValue?.frames.find((f) => f.ts === ts) ?? this.currentFrame;
    this.onDataChanged();
    this.logSwap(ts, level, swapStart);
  }

  private paint(result: StationDecodeResult): void {
    const url = timeSync("colorize", () =>
      colorizeMosaicRaster(result.raw, result.width, result.height, this.floorEnabled ? PRECIP_FLOOR_RAW : NO_FLOOR_RAW),
    );
    const corners = cornersFromBBox3857([result.bbox.minX, result.bbox.minY, result.bbox.maxX, result.bbox.maxY]);
    const nextSlot: 0 | 1 = this.activeSlot === 0 ? 1 : 0;
    const nextSourceId = SOURCE_IDS[nextSlot];
    const source = this.map.getSource(nextSourceId) as maplibregl.ImageSource | undefined;
    source?.updateImage({ url, coordinates: cornersToMapLibreCoordinates(corners) });

    if (this.active) {
      this.map.setPaintProperty(layerIdFor(nextSourceId), "raster-opacity", MOSAIC_OPACITY);
      this.map.setPaintProperty(layerIdFor(SOURCE_IDS[this.activeSlot]), "raster-opacity", 0);
    }
    this.activeSlot = nextSlot;
  }

  private clearFrame(): void {
    if (this.currentTs === null) return;
    this.currentTs = null;
    this.currentLevel = null;
    this.currentFrame = null;
    this.lastResult = null;
    if (this.layersReady) this.map.setPaintProperty(layerIdFor(SOURCE_IDS[this.activeSlot]), "raster-opacity", 0);
    this.onDataChanged();
  }

  /** "preload the next 1-2 frames in loop mode", same discipline as
   * FusionController.preloadAhead: fire-and-forget decodes at the SAME
   * level as the just-displayed frame, so swapTo() is a cache hit by the
   * time playback/scrubbing reaches them. */
  private preloadAhead(fromMs: number, level: OverviewLevel): void {
    const frames = this.manifestValue?.frames;
    if (!frames) return;
    const startIdx = frames.findIndex((f) => f.dataTimeLastMs > fromMs);
    if (startIdx < 0) return;
    for (let k = 0; k < PRELOAD_AHEAD; k++) {
      const f = frames[startIdx + k];
      if (!f) break;
      if (mosaicFrameCache.has(decodeCacheKey(f.ts, level))) continue;
      void this.decoder
        .decode("MOSAIC", mosaicFrameUrl(this.getBaseUrl(), f.ts), level, decodeCacheKey(f.ts, level))
        .then((r) => setDecodeResult(mosaicFrameCache, f.ts, level, r))
        .catch(() => {});
    }
  }

  private logSwap(ts: string, level: OverviewLevel, swapStart: number): void {
    if (!DEV) return;
    const ms = performance.now() - swapStart;
    if (!this.firstPaintLogged) {
      this.firstPaintLogged = true;
      performance.mark("mosaic:first-paint");
      try {
        const measure = performance.measure("mosaic:cold-load", "radar:app-init", "mosaic:first-paint");
        // eslint-disable-next-line no-console
        console.debug(`[mosaic] cold-load (app init -> first mosaic paint): ${measure.duration.toFixed(1)}ms`);
      } catch {
        /* radar:app-init mark missing (e.g. hot-reload) — skip the cold-load number */
      }
    }
    // eslint-disable-next-line no-console
    console.debug(`[mosaic] frame swap ts=${ts} level=${level}: ${ms.toFixed(1)}ms`);
  }

  private logBytes(key: string, bytes: number): void {
    if (!DEV) return;
    // eslint-disable-next-line no-console
    console.debug(`[mosaic] decoded ${key}: ${(bytes / 1024).toFixed(1)} KiB over the wire`);
  }
}
